// @givmo/compliance — Givmo’s filing desk as the backend serves it (CCM-R1-DESK-WEB): the case's
// `desk` block and `submission_mode` on the submission read, and the organization's standing
// agreement (`GET …/desk-authorization`). Pure: the screens read what is served and never infer a
// state the read doesn't hold (contract §9.3; the backend's `charity_compliance.md` § The filing
// desk).

import { R5, W1, W2, W4, W6, W8, w3, w5, w7 } from './desk-words';

export type DeskAssignmentState =
  | 'queued'
  | 'in_progress'
  | 'awaiting_organization'
  | 'ready_to_dispatch'
  | 'dispatched'
  | 'completed'
  | 'returned'
  | 'cancelled';

const DESK_ASSIGNMENT_STATES: readonly DeskAssignmentState[] = [
  'queued',
  'in_progress',
  'awaiting_organization',
  'ready_to_dispatch',
  'dispatched',
  'completed',
  'returned',
  'cancelled',
];

/** The states in which Givmo's desk has the filing (`submission_mode` is `givmo` exactly then). */
const DESK_OPEN_STATES: ReadonlySet<DeskAssignmentState> = new Set([
  'queued',
  'in_progress',
  'awaiting_organization',
  'ready_to_dispatch',
  'dispatched',
]);

export type DeskNeedKind = 'fact' | 'signature' | 'authorization';

/** The case's latest assignment to the desk, open or closed. */
export interface DeskAssignment {
  state: DeskAssignmentState | 'unknown';
  handedByUserId?: string;
  handedAt?: string;
  /** What the desk needs to go on: its kind, and W3's need as served (W3a–W3c). */
  needKind?: DeskNeedKind | 'unknown';
  needWords?: string;
  closingReason?: string;
  /** The desk's own one-line reason for handing the filing back (W7). */
  returnReason?: string;
}

/** The desk on one case, and what the viewer may do with it. */
export interface DeskCase {
  /** `submission_mode` as served: `givmo` exactly while the desk's latest assignment is open. */
  givmoHasIt: boolean;
  /** Whether the desk takes this filing (the support matrix, and an initial or renewal case). */
  supported: boolean;
  /** Whether the organization's agreement in force covers it. */
  standingCovers: boolean;
  /** Whether the block serves an agreement in force at all (`standing`); none once it is off. */
  standingInForce: boolean;
  assignment?: DeskAssignment;
  abilities: { handOver: boolean; takeBack: boolean; resume: boolean };
}

/** One filing the desk takes, by its jurisdiction and rule. */
export interface DeskFiling {
  jurisdictionCode: string;
  ruleId: string;
}

/** The organization's agreement for Givmo to file (its standing authorization). */
export interface DeskStanding {
  state: 'active' | 'superseded' | 'revoked' | 'unknown';
  recordedAt?: string;
  revokedAt?: string;
}

/** The standing read: the agreement in force, if any, who may turn it off, and what it covers. */
export interface DeskStandingRead {
  standing?: DeskStanding;
  canRevoke: boolean;
  coveredFilings: DeskFiling[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const optionalStr = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

function decodeAssignment(raw: unknown): DeskAssignment | undefined {
  if (!isRecord(raw)) return undefined;
  const need = isRecord(raw.need) ? raw.need : undefined;
  const optional = (key: keyof DeskAssignment, value: unknown) => {
    const decoded = optionalStr(value);
    return decoded !== undefined ? { [key]: decoded } : {};
  };
  return {
    state: oneOf(raw.state, DESK_ASSIGNMENT_STATES) ?? 'unknown',
    ...optional('handedByUserId', raw.handed_by_user_id),
    ...optional('handedAt', raw.handed_at),
    ...(need !== undefined
      ? {
          needKind: oneOf(need.kind, ['fact', 'signature', 'authorization'] as const) ?? 'unknown',
          ...optional('needWords', need.words),
        }
      : {}),
    ...optional('closingReason', raw.closing_reason),
    ...optional('returnReason', raw.return_reason),
  };
}

/** The `desk` block of a case's submission read; none where the read serves none (the desk dark). */
export function decodeDeskCase(submission: unknown): DeskCase | undefined {
  if (!isRecord(submission) || !isRecord(submission.desk)) return undefined;
  const desk = submission.desk;
  const abilities = isRecord(desk.abilities) ? desk.abilities : {};
  const assignment = decodeAssignment(desk.assignment);
  return {
    givmoHasIt: submission.submission_mode === 'givmo',
    supported: desk.supported === true,
    standingCovers: desk.standing_covers === true,
    standingInForce: isRecord(desk.standing),
    ...(assignment !== undefined ? { assignment } : {}),
    abilities: {
      handOver: abilities.hand_over === true,
      takeBack: abilities.take_back === true,
      resume: abilities.resume === true,
    },
  };
}

/** The standing read (`GET …/desk-authorization`); none where the body isn't one. */
export function decodeDeskStanding(body: unknown): DeskStandingRead | undefined {
  if (!isRecord(body)) return undefined;
  const standing = isRecord(body.standing) ? body.standing : undefined;
  const covered = Array.isArray(body.covered_filings) ? body.covered_filings : [];
  return {
    ...(standing !== undefined
      ? {
          standing: {
            state: oneOf(standing.state, ['active', 'superseded', 'revoked'] as const) ?? 'unknown',
            ...(optionalStr(standing.recorded_at) !== undefined
              ? { recordedAt: optionalStr(standing.recorded_at) }
              : {}),
            ...(optionalStr(standing.revoked_at) !== undefined
              ? { revokedAt: optionalStr(standing.revoked_at) }
              : {}),
          },
        }
      : {}),
    canRevoke: body.can_revoke === true,
    coveredFilings: covered.flatMap((each) => {
      if (!isRecord(each)) return [];
      const jurisdictionCode = optionalStr(each.jurisdiction_code);
      const ruleId = optionalStr(each.rule_id);
      return jurisdictionCode !== undefined && ruleId !== undefined
        ? [{ jurisdictionCode, ruleId }]
        : [];
    }),
  };
}

/**
 * Whether Givmo's desk has the case now: the read's `submission_mode` says so, which also holds for
 * an open state this build doesn't know; or its latest assignment is open.
 */
export function deskHolds(desk: DeskCase | undefined): boolean {
  if (desk === undefined) return false;
  const state = desk.assignment?.state;
  return (
    desk.givmoHasIt || (state !== undefined && state !== 'unknown' && DESK_OPEN_STATES.has(state))
  );
}

/**
 * Q1: whether "Who files it" shows. On a year with an open case, where its desk block says Givmo can
 * file it under an agreement that covers it, or the desk has it or had it; on a year with no case
 * yet, where the agreement in force covers the filing's rule (a year's case is always an initial or
 * a renewal one, both kinds the desk takes). Only an agreement in force covers: the read shows the
 * latest one once it is turned off, for the settings card, and that one offers nothing. Nowhere
 * else: nobody can agree in the product yet, so the offer would end at R1 with nothing to press.
 */
export function deskOffered(
  desk: DeskCase | undefined,
  hasCase: boolean,
  standing: DeskStandingRead | undefined,
  filing: { jurisdictionCode: string; ruleId: string | undefined } | undefined,
): boolean {
  if (hasCase) {
    if (desk === undefined || !desk.supported) return false;
    return desk.standingCovers || desk.assignment !== undefined;
  }
  if (standing?.standing?.state !== 'active' || filing === undefined || filing.ruleId === undefined)
    return false;
  return standing.coveredFilings.some(
    (each) =>
      each.jurisdictionCode.toUpperCase() === filing.jurisdictionCode.toUpperCase() &&
      each.ruleId === filing.ruleId,
  );
}

/**
 * The desk's status line for its latest assignment (W1–W8, R5; D-CCM-67), or none: a state this
 * build doesn't know says nothing rather than something more confident. `sentDay` is W5's day, the
 * desk's send the email service took.
 */
export function deskStatusLine(
  assignment: DeskAssignment | undefined,
  sentDay: string | undefined,
): string | undefined {
  switch (assignment?.state) {
    case 'queued':
      return W1;
    case 'in_progress':
      return W2;
    case 'awaiting_organization':
      return assignment.needWords !== undefined ? w3(assignment.needWords) : undefined;
    case 'ready_to_dispatch':
      return W4;
    case 'dispatched':
      return sentDay !== undefined ? w5(sentDay) : undefined;
    case 'completed':
      return W6;
    case 'returned':
      return assignment.returnReason !== undefined ? w7(assignment.returnReason) : undefined;
    case 'cancelled':
      return assignment.closingReason === 'authorization_revoked' ? R5 : W8;
    default:
      return undefined;
  }
}

/** Whether the desk's need is the organization's authorization (W3c). */
export function needsAuthorization(desk: DeskCase | undefined): boolean {
  return (
    desk?.assignment?.state === 'awaiting_organization' &&
    desk.assignment.needKind === 'authorization'
  );
}
